import React, { useCallback, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Snackbar,
  Stack,
  Typography
} from '@mui/material';
import { Refresh } from '@mui/icons-material';

import { buildComingSoonColumns, buildUpdateColumns } from './columns';
import ComingSoonDialog from './ComingSoonDialog';
import ConfigSection from './ConfigSection';
import ConfirmDeleteDialog from './ConfirmDeleteDialog';
import UpdateDialog from './UpdateDialog';
import { WhatsNewComingSoonItem, WhatsNewUpdate } from './types';
import { ListName, useWhatsNewConfig } from './useWhatsNewConfig';
import { formatDisplayDateTime } from './utils';

type PendingDeletion = { list: ListName; id: string; title: string } | null;

const WhatsNewRegain = () => {
  const {
    updates,
    comingSoon,
    metadata,
    loading,
    saving,
    loadError,
    configLoaded,
    isDirty,
    toast,
    setToast,
    fetchConfig,
    saveUpdate,
    saveComingSoon,
    removeEntry,
    publish
  } = useWhatsNewConfig();

  const [editingUpdate, setEditingUpdate] = useState<WhatsNewUpdate | null>(null);
  const [updateDialogOpen, setUpdateDialogOpen] = useState(false);
  const [editingComingSoon, setEditingComingSoon] = useState<WhatsNewComingSoonItem | null>(null);
  const [comingSoonDialogOpen, setComingSoonDialogOpen] = useState(false);
  const [pendingDeletion, setPendingDeletion] = useState<PendingDeletion>(null);

  const closeUpdateDialog = useCallback(() => {
    setUpdateDialogOpen(false);
    setEditingUpdate(null);
  }, []);

  const closeComingSoonDialog = useCallback(() => {
    setComingSoonDialogOpen(false);
    setEditingComingSoon(null);
  }, []);

  const handleSaveUpdate = useCallback(
    (update: WhatsNewUpdate) => {
      saveUpdate(update);
      closeUpdateDialog();
    },
    [saveUpdate, closeUpdateDialog]
  );

  const handleSaveComingSoon = useCallback(
    (item: WhatsNewComingSoonItem) => {
      saveComingSoon(item);
      closeComingSoonDialog();
    },
    [saveComingSoon, closeComingSoonDialog]
  );

  const confirmDeletion = useCallback(() => {
    if (!pendingDeletion) {
      return;
    }
    removeEntry(pendingDeletion.list, pendingDeletion.id);
    setPendingDeletion(null);
  }, [pendingDeletion, removeEntry]);

  const updateColumns = useMemo(
    () =>
      buildUpdateColumns({
        onEdit: row => {
          setEditingUpdate(row);
          setUpdateDialogOpen(true);
        },
        onDelete: row => setPendingDeletion({ list: 'updates', id: row.id, title: row.title })
      }),
    []
  );

  const comingSoonColumns = useMemo(
    () =>
      buildComingSoonColumns({
        onEdit: row => {
          setEditingComingSoon(row);
          setComingSoonDialogOpen(true);
        },
        onDelete: row => setPendingDeletion({ list: 'coming_soon', id: row.id, title: row.title })
      }),
    []
  );

  const updateIds = useMemo(() => updates.map(item => item.id), [updates]);
  const comingSoonIds = useMemo(() => comingSoon.map(item => item.id), [comingSoon]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, mt: 10 }}>
      <Stack
        direction='row'
        alignItems='flex-start'
        justifyContent='space-between'
        spacing={2}
        sx={{ mb: 2 }}>
        <Box>
          <Typography variant='h5'>What&apos;s New in REGAIN</Typography>
          {metadata ? (
            <Typography variant='caption' color='text.secondary'>
              Last saved by {metadata.updated_by} on {formatDisplayDateTime(metadata.updated_at)}
            </Typography>
          ) : null}
        </Box>
        <Stack direction='row' spacing={1} alignItems='center'>
          {isDirty ? <Chip size='small' color='warning' label='Unsaved changes' /> : null}
          <Button startIcon={<Refresh />} onClick={fetchConfig} disabled={saving}>
            Reload
          </Button>
          <Button
            variant='contained'
            onClick={publish}
            disabled={!configLoaded || !isDirty || saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </Stack>
      </Stack>

      {loadError ? (
        <Alert
          severity='error'
          sx={{ mb: 2 }}
          action={<Button onClick={fetchConfig}>Retry</Button>}>
          {loadError}
          {!configLoaded
            ? ' Editing is disabled until the current configuration loads, so a partial save cannot replace it.'
            : ''}
        </Alert>
      ) : null}

      <ConfigSection
        title='Updates'
        addLabel='Add update'
        rows={updates}
        columns={updateColumns}
        canEdit={configLoaded}
        onAdd={() => {
          setEditingUpdate(null);
          setUpdateDialogOpen(true);
        }}
        sx={{ mb: 3 }}
      />

      <ConfigSection
        title='Coming soon'
        addLabel='Add item'
        rows={comingSoon}
        columns={comingSoonColumns}
        canEdit={configLoaded}
        onAdd={() => {
          setEditingComingSoon(null);
          setComingSoonDialogOpen(true);
        }}
      />

      <UpdateDialog
        open={updateDialogOpen}
        update={editingUpdate}
        existingIds={updateIds}
        onClose={closeUpdateDialog}
        onSave={handleSaveUpdate}
      />

      <ComingSoonDialog
        open={comingSoonDialogOpen}
        item={editingComingSoon}
        existingIds={comingSoonIds}
        onClose={closeComingSoonDialog}
        onSave={handleSaveComingSoon}
      />

      <ConfirmDeleteDialog
        open={Boolean(pendingDeletion)}
        title={pendingDeletion?.title ?? ''}
        onCancel={() => setPendingDeletion(null)}
        onConfirm={confirmDeletion}
      />

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={6000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert
          severity={toast?.severity ?? 'success'}
          onClose={() => setToast(null)}
          sx={{ width: '100%' }}>
          {toast?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default WhatsNewRegain;
